import {useQuery} from '@tanstack/react-query';
import {useMemo} from 'react';

import {
    isCertificateAvailableForCourse,
    type CourseCertificateEligibilityInput,
} from 'src/components/grow-with-google/learning-path/utils/course-certificate';
import {udemyApi} from 'src/lib/ud-global-fetch';

type CourseCertificateResponseItem = {
    course_id?: number | string | null;
    is_ready?: boolean | null;
    long_url?: string | null;
    type?: string | null;
    completion_date?: string | null;
    path_id?: string | number | null;
};

type CourseCertificateResponse = {
    results?: CourseCertificateResponseItem[];
};

export type CourseCertificateInfo = {
    url: string;
    completionDate?: string;
    isReady: boolean;
};

type CertificateData = Record<string, CourseCertificateInfo>;

const CERTIFICATE_URL_FIELDS = ['long_url'] as const;

function getCertificateUrl(item: CourseCertificateResponseItem) {
    for (const key of CERTIFICATE_URL_FIELDS) {
        const value = item[key];
        if (typeof value === 'string' && value.length > 0) {
            return value;
        }
    }
    return undefined;
}

function getCompletionDate(item: CourseCertificateResponseItem): string | undefined {
    const value = item?.completion_date;
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function getCourseId(item: CourseCertificateResponseItem): string | undefined {
    const value = item?.course_id;
    if (value === null || value === undefined) {
        return undefined;
    }
    const id = typeof value === 'number' ? String(value) : value;
    return id.length > 0 ? id : undefined;
}

export function useCourseCertificateUrlById(
    courseIds: string[],
    enabled: boolean,
    courseById: Record<string, CourseCertificateEligibilityInput | undefined>,
    progressById: Record<string, number | null | undefined>,
    pathId: number,
) {
    const normalizedCourseIds = useMemo(() => Array.from(new Set(courseIds)).sort(), [courseIds]);
    const eligibleCourseIds = useMemo(
        () =>
            normalizedCourseIds.filter((courseId) => {
                const course = courseById[courseId];
                if (!course) {
                    return false;
                }
                const isCourseCompleted = (progressById[courseId] ?? 0) >= 100;
                return isCertificateAvailableForCourse(course, isCourseCompleted);
            }),
        [normalizedCourseIds, courseById, progressById],
    );
    const shouldFetch = enabled && eligibleCourseIds.length > 0;

    const query = useQuery<CertificateData>({
        queryKey: ['course-certificate-urls', eligibleCourseIds, pathId],
        enabled: shouldFetch,
        queryFn: async () => {
            const response = await udemyApi.get<CourseCertificateResponse>(
                '/users/me/certificates/',
                {
                    params: {
                        course_id: eligibleCourseIds,
                    },
                },
            );
            const results = response.data?.results ?? [];
            return results.reduce<CertificateData>((acc, item) => {
                const url = getCertificateUrl(item) ?? '';
                const completionDate = getCompletionDate(item);
                const courseId = getCourseId(item);
                const isReady = item.is_ready === true;
                const info: CourseCertificateInfo = {url, completionDate, isReady};
                const isGoogleCourse =
                    item.type === 'google_course' || item.type == null || item.type === '';
                if (courseId && isGoogleCourse) {
                    acc[courseId] = info;
                }
                const itemPathId = item.path_id != null ? String(item.path_id) : undefined;
                if (
                    item.type === 'google_path' &&
                    itemPathId != null &&
                    Number(itemPathId) === pathId
                ) {
                    acc['google_path'] = info;
                }
                return acc;
            }, {});
        },
    });

    const data = query.data;
    const certificateById = data ?? {};

    return {
        certificateById,
        query,
        trackerCertificate: certificateById['google_path']
            ? {
                  certificateUrl: certificateById['google_path'].url,
                  completionDate: certificateById['google_path'].completionDate,
                  isReady: certificateById['google_path'].isReady,
              }
            : undefined,
    };
}
